import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import { useDebounce } from './useDebounce';
import { useJsonHeaders } from './useJsonHeaders';
import { getGlobalConfig } from '../global/ConfigProvider';

export interface Option {
  id: string;
  label: string;
}

interface Props<I, D, O extends Option> {
  url: string;
  value?: string;
  initialData?: Partial<O>;
  getDataArray?: (data: D) => Array<I>;
  getOption?: (item: I) => O;
  getExtraOptions?: (q: string, options: O[]) => O[];
  getParams?: (q: string) => Record<string, unknown>;
  filterOption?: (item: O) => boolean;
}

export function useAutocomplete<
  I = Option,
  D = I[],
  O extends Option = Option
>({
  url,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getDataArray = (data) => data as any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getOption = (option) => option as any,
  getExtraOptions = () => [],
  getParams = (q) => ({ q }),
  filterOption = (o) => Boolean(o),
  value,
  initialData,
}: Props<I, D, O>) {
  const { baseApiUrl } = getGlobalConfig();
  const defaultValObj =
    value && initialData ? { id: value, ...(initialData as any) } : undefined;
  const [options, setOptions] = useState<O[]>(
    defaultValObj ? [defaultValObj] : []
  );
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useDebounce('');
  const headers = useJsonHeaders();

  const query = useCallback(
    (text: string) => {
      const newOptions: O[] = [];
      return axios({
        method: 'get',
        url: `${baseApiUrl}/${url}`,
        params: getParams(text),
        headers,
      })
        .then((res) => res.data)
        .then(getDataArray)
        .then((items) => {
          newOptions.push(...items.map(getOption).filter(filterOption));
          newOptions.push(...getExtraOptions(text, newOptions));
        })
        .then(() => newOptions);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value]
  );

  useEffect(() => {
    setLoading(true);
    const newOptions: O[] = [];
    query(searchText)
      .then((opts) => {
        newOptions.push(...opts);
      })
      .finally(() => {
        setOptions((oldOptions) => {
          if (!newOptions.find((opt) => opt.id === value)) {
            const selectedOption = oldOptions.find((opt) => opt.id === value);
            if (selectedOption) {
              return [...newOptions, selectedOption];
            }
          }
          return newOptions;
        });
        setLoading(false);
      });
  }, [searchText, value, query]);

  return [options, setSearchText, loading, query] as const;
}
